import SquaberIntervalMap from "../TradingView/SquaberIntervalMap";
import { TV_CHART_BRIDGE_ACTION } from "./actions";
import serializeTvChartBridgeShapes from "./serializeTvChartBridgeShapes";

/**
 * Maps HTML ProxyDataFeed postMessage RPC onto a real TVDataFeed instance.
 */
export default function createShellDataFeedBridge({
  dataFeed,
  postMessage,
  onShapesUpdated
}) {
  const subscriptionsByUid = new Map();
  let active = true;
  let lastResolution = null;

  const send = payload => {
    postMessage(JSON.stringify(payload));
  };

  const respond = (requestId, result, error = null) => {
    send({
      action: TV_CHART_BRIDGE_ACTION.DF_RESPONSE,
      requestId,
      data: error
        ? { ok: false, error }
        : { ok: true, result }
    });
  };

  const pushShapesUpdate = () => {
    if (typeof dataFeed?.getShapesSnapshot !== "function") {
      return;
    }

    const shapes = serializeTvChartBridgeShapes(dataFeed.getShapesSnapshot());
    const actualIntervalTypeId =
      lastResolution != null ? SquaberIntervalMap[lastResolution] : undefined;
    const payload = {
      action: TV_CHART_BRIDGE_ACTION.SHAPES_UPDATE,
      data: {
        shapes,
        ...(actualIntervalTypeId != null ? { actualIntervalTypeId } : {})
      }
    };

    send(payload);
    if (typeof onShapesUpdated === "function") {
      onShapesUpdated(shapes);
    }
  };

  const subscribeBars = ({ symbolInfo, resolution, subscriberUID }) => {
    const existingSubscription = subscriptionsByUid.get(subscriberUID);
    if (existingSubscription && active) {
      dataFeed.unsubscribeBars(subscriberUID);
    }

    const onRealtimeCallback = bar => {
      send({
        action: TV_CHART_BRIDGE_ACTION.DF_REALTIME_BAR,
        data: {
          subscriberUID,
          bar
        }
      });
    };

    subscriptionsByUid.set(subscriberUID, {
      symbolInfo,
      resolution,
      onRealtimeCallback
    });

    if (active) {
      dataFeed.subscribeBars(
        symbolInfo,
        resolution,
        onRealtimeCallback,
        subscriberUID,
        () => {}
      );
    }
  };

  const unsubscribeBars = subscriberUID => {
    const hadSubscription = subscriptionsByUid.has(subscriberUID);

    if (!hadSubscription) {
      return;
    }

    subscriptionsByUid.delete(subscriberUID);

    if (active) {
      dataFeed.unsubscribeBars(subscriberUID);
    }
  };

  const handleMessage = message => {
    if (!message || !message.action) {
      return false;
    }

    const { action, requestId, data = {} } = message;

    switch (action) {
      case TV_CHART_BRIDGE_ACTION.DF_ON_READY: {
        dataFeed.onReady(configurationData => {
          respond(requestId, configurationData);
        });
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.DF_RESOLVE_SYMBOL: {
        dataFeed.resolveSymbol(
          data.symbolName,
          symbolInfo => respond(requestId, symbolInfo),
          error => respond(requestId, null, error || "resolveSymbol failed")
        );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.DF_GET_BARS: {
        lastResolution = data.resolution;
        dataFeed.getBars(
          data.symbolInfo,
          data.resolution,
          data.periodParams,
          (bars, meta) => {
            respond(requestId, {
              bars,
              meta: meta || {}
            });
            pushShapesUpdate();
          },
          error => respond(requestId, null, error || "getBars failed")
        );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.DF_SUBSCRIBE_BARS: {
        subscribeBars({
          symbolInfo: data.symbolInfo,
          resolution: data.resolution,
          subscriberUID: data.subscriberUID
        });
        respond(requestId, { subscribed: true });
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.DF_UNSUBSCRIBE_BARS: {
        unsubscribeBars(data.subscriberUID);
        respond(requestId, { unsubscribed: true });
        return true;
      }

      default:
        return false;
    }
  };

  const setActive = nextActive => {
    if (active === nextActive) {
      return;
    }

    active = nextActive;

    if (active) {
      subscriptionsByUid.forEach(
        ({ symbolInfo, resolution, onRealtimeCallback }, subscriberUID) => {
          dataFeed.subscribeBars(
            symbolInfo,
            resolution,
            onRealtimeCallback,
            subscriberUID,
            () => {}
          );
        }
      );
      return;
    }

    subscriptionsByUid.forEach((_, subscriberUID) => {
      dataFeed.unsubscribeBars(subscriberUID);
    });
  };

  const destroy = () => {
    subscriptionsByUid.forEach((_, subscriberUID) => {
      dataFeed.unsubscribeBars(subscriberUID);
    });
    subscriptionsByUid.clear();
  };

  const pushShapesNow = () => {
    pushShapesUpdate();
  };

  return { handleMessage, setActive, destroy, pushShapesNow };
}
